import { Form } from "antd";
import { PermissionForm } from "./PlatformPermissionsFormDialog.default";
import { useEffect, useState } from "react";
import {
  addPlatformRolePermission,
  updatePlatformRolePermission,
  deletePlatformRolePermission,
  getPermissions,
  getPlatformRoles,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import {
  PlatformPermission,
  PermissionActionType,
  Permission,
  PlatformRole,
} from "../repository/data.default";
import { InputObject } from "../controls/default";
import { retry, getProblemMessage } from "../utils/http";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";

export interface PlatformPermissionsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: PlatformPermission;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function PlatformPermissionsFormDialog(
  props: PlatformPermissionsFormDialogProps,
) {
  const [form] = Form.useForm();
  const values = Form.useWatch<PermissionForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      } else {
        form.setFieldsValue(props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { platformRole, action, permission } = values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addPlatformRolePermission({
        applicationRoleUid: platformRole.value,
        allowedAction: action.value,
        permissionUid: permission.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updatePlatformRolePermission({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          allowedAction: action.value,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deletePlatformRolePermission(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(undefined);
    form.resetFields();
  }

  const actionsOptions = async (): Promise<InputObject[]> => {
    const options = Object.entries(PermissionActionType).map(
      ([value, display]) => ({
        display,
        value,
      }),
    );

    return options;
  };

  const permissionRequest = (search: string) => {
    return retry(() =>
      getPermissions({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Permission>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const platformRolesRequest = (search: string) => {
    return retry(() =>
      getPlatformRoles({
        searchQuery: {
          conditions: [
            {
              field: nameOf<PlatformRole>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Permission", undefined)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode === "add" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "platformRole",
                    label: "Platform Role",
                    request: platformRolesRequest,
                    rules: [
                      {
                        required: true,
                        message: "Aplication Role is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "action",
                    label: "Action",
                    request: actionsOptions,
                    rules: [
                      {
                        required: true,
                        message: "Action is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "permission",
                    label: "Permission",
                    request: permissionRequest,
                    rules: [
                      {
                        required: true,
                        message: "Permission is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "update" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "action",
                    label: "Action",
                    request: actionsOptions,
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="permission"
          entityName={props.initialValues?.applicationRoleName}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default PlatformPermissionsFormDialog;
